import IconButton from '@/components/Buttons/IconButton';
import LoadingAnimation from '@/images/LoadingAnimation.json';
import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import { Typography } from '@mui/material';
import { useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import pause from './images/pauseoutline.svg';
import play from './images/playoutline.svg';

const PlayerContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

const LoadingContainer = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
`;

const Loading = styled(Player)`
    height: 30px;
`;

const PlayPauseIcon = styled.img`
    width: 24px;
    height: 24px;
`;

const LabelLoading = styled.span`
    font-size: 14px;
`;

const padDigits = (num: number): string => {
    return num.toString().padStart(2, '0');
};

const formatSeconds = (seconds: number): string => {
    const minutes: number = Math.floor(seconds / 60);
    const remainingSeconds: number = seconds % 60;
    return `${padDigits(minutes)}:${padDigits(remainingSeconds)}`;
};

function RecordingPlayer({ duration, isUploading, listenUrl }: { duration: number; isUploading: boolean; listenUrl?: string }) {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<ReactPlayer>(null);
    const [position, setPosition] = useState<number>(0);
    function handlePlay() {
        setIsPlaying(true);
    }

    function handlePause() {
        setIsPlaying(false);
    }

    if (isUploading) {
        return (
            <LoadingContainer>
                <Loading src={LoadingAnimation} autoplay loop />
                <LabelLoading>Record is uploading</LabelLoading>
            </LoadingContainer>
        );
    }
    if (!isPlaying) {
        return (
            <PlayerContainer>
                <IconButton ariaLabel="play audio" onClick={handlePlay}>
                    <PlayPauseIcon src={play} />
                </IconButton>
                <Typography variant="body1" fontWeight={600}>
                    {formatSeconds(Math.round(duration))}
                </Typography>
            </PlayerContainer>
        );
    }

    return (
        <PlayerContainer>
            <IconButton ariaLabel="pause audio" onClick={handlePause}>
                <PlayPauseIcon src={pause} />
            </IconButton>
            <Typography variant="body1" fontWeight={600}>
                {formatSeconds(Math.round(position))} / {formatSeconds(Math.round(duration))}
            </Typography>
            <ReactPlayer
                style={{ display: 'none' }}
                url={listenUrl}
                ref={audioRef}
                onProgress={state => setPosition(Math.ceil(state.playedSeconds * 1000) / 1000)}
                controls={false}
                playing={isPlaying}
                progressInterval={1}
                onEnded={() => setIsPlaying(false)}
            />
        </PlayerContainer>
    );
}

export default RecordingPlayer;
